import React, { useState, useEffect } from 'react';
import { FlashCard } from '../components/FlashCard';
import { flashCardApi, type FlashCard as FlashCardType } from '../services/api';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Plus, Filter, AlertCircle } from 'lucide-react';
import { useToast } from '../components/ui/use-toast';

const MOCK_FLASHCARDS: FlashCardType[] = [
  {
    id: '1',
    question: 'What is Big O notation?',
    answer: 'Big O notation describes the upper bound of the growth rate of an algorithm.',
    category: 'algorithms'
  },
  {
    id: '2',
    question: 'What is a Binary Search Tree?',
    answer: 'A binary tree where each nodes left subtree has values less than the node, and right subtree has values greater than the node.',
    category: 'data structures'
  },
  {
    id: '3',
    question: 'What is recursion?',
    answer: 'A programming concept where a function calls itself to solve a problem by breaking it down into smaller subproblems.',
    category: 'programming'
  }
];

export function FlashcardsPage() {
  const [flashcards, setFlashcards] = useState<FlashCardType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const loadFlashcards = async () => {
      try {
        // In development, use mock data since we don't have a backend
        // const data = await flashCardApi.getAll();
        const data = MOCK_FLASHCARDS;
        setFlashcards(data);
        setError(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load flashcards';
        setError(message);
        toast({
          title: "Error",
          description: message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadFlashcards();
  }, []);

  const categories = ['all', ...new Set(flashcards.map(card => card.category))];
  const filteredCards = selectedCategory === 'all' 
    ? flashcards 
    : flashcards.filter(card => card.category === selectedCategory);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load flashcards</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Breadcrumbs 
            items={[
              { label: 'Study Tools', href: '/tools' },
              { label: 'Flashcards' }
            ]} 
          />
          
          <div className="mt-4 flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Flashcards</h1>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center space-x-2 hover:bg-blue-700">
              <Plus className="w-5 h-5" />
              <span>Create Flashcard</span>
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center space-x-2 overflow-x-auto pb-2">
            <Filter className="w-5 h-5 text-gray-500" />
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading flashcards...</p>
          </div>
        ) : filteredCards.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No flashcards found for this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCards.map((flashcard) => (
              <FlashCard
                key={flashcard.id}
                question={flashcard.question}
                answer={flashcard.answer}
                category={flashcard.category}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}